// src/features/ucits-etf/store/useScreenerStore.ts
//
// Persisted Zustand store for the UCITS screener filter choices. Returning to
// the dashboard restores the last-used filters from localStorage so users
// don't have to re-dial their screen every visit.
//
// Design notes:
//   - Only the filter VALUES are persisted (via `partialize`). Ephemeral view
//     state like column sort stays in the component, never in localStorage.
//   - Selectors read individual primitives (and the stable `selectedSectors`
//     array reference) so components never subscribe to the whole object —
//     this avoids the new-object-every-render infinite-loop footgun.
//   - persist is resilient by construction: a corrupt / absent entry simply
//     falls back to DEFAULT_FILTERS, and `version` + a no-op migrate guard
//     against future schema drift. It never throws into the render path.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { DEFAULT_FILTERS, type ScreenerFilters } from '../lib/screener';

interface ScreenerStore extends ScreenerFilters {
    /** Shallow-merge a partial filter patch. */
    setFilters: (patch: Partial<ScreenerFilters>) => void;
    /** Restore every filter to its canonical default. */
    reset: () => void;
}

export const useScreenerStore = create<ScreenerStore>()(
    persist(
        (set) => ({
            ...DEFAULT_FILTERS,
            setFilters: (patch) => set(patch),
            reset: () => set({ ...DEFAULT_FILTERS }),
        }),
        {
            name: 'forge:ucits-screener-filters',
            version: 1,
            // Persist ONLY the serialisable filter values, never the actions.
            partialize: (state): ScreenerFilters => ({
                maxExpense: state.maxExpense,
                minAumMil: state.minAumMil,
                minVolK: state.minVolK,
                ucitsOnly: state.ucitsOnly,
                selectedSectors: state.selectedSectors,
                dividendPolicy: state.dividendPolicy,
            }),
        },
    ),
);
